<template>
  <Nav />
  <div class="container">
    <div class="municipio-container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1>Municípios</h1>
        <button class="btn btn-secondary" @click="showAddMunicipioModal" v-if="user && user.role === 'admin'">
          Novo Município
        </button>
      </div>
      <div class="municipio-search mb-3">
        <div class="filter-container-municipios mb-0">
          <input 
            type="text" 
            id="searchMunicipios"
            class="form-control" 
            placeholder="Buscar municípios, código IBGE..." 
            v-model="searchText"
            @keyup.enter="search"
          >
          <button class="btn btn-secondary" @click="search">Buscar</button>
          <button class="btn btn-outline-secondary" @click="clearFilter">Limpar</button>
        </div>
        <div class="form-check check-officials">
          <input class="form-check-input small-officials-checkbox" type="checkbox" v-model="showOnlyOfficial" id="showOnlyOfficial">
          <label class="form-check-label small-label-officials-checkbox" for="showOnlyOfficial">
            Apenas oficiais
          </label>
        </div>
      </div>
      <div class="municipio-body">
        <div class="table-responsive">
          <table class="table municipios-table">
          <thead>
            <tr>
              <th scope="col" class="municipio-col">Município</th>
              <th scope="col" class="ibge-col">Código IBGE</th>
              <th scope="col" class="demo-col">Tipo</th>
              <th scope="col" class="actions-col" v-if="user && user.role === 'admin'">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="municipio in municipios" :key="municipio.codigo_siconfi">
              <td class="municipio-col">{{ municipio.uf }} - {{ municipio.razao_social ? municipio.razao_social.replace("MUNICÍPIO DE ", "") : municipio.cidade }}</td>
              <td class="ibge-col">{{ municipio.codigo_ibge || '-' }}</td>
              <td class="demo-col">
                <span class="badge" :class="municipio.demo ? 'bg-secondary' : 'bg-success'">
                  {{ municipio.demo ? 'Demonstração' : 'Oficial' }}
                </span>
              </td>
              <td class="actions-col" v-if="user && user.role === 'admin'">
                <div class="d-flex align-items-center justify-content-end">
                  <button class="btn-icon me-2" @click="showEditMunicipioModal(municipio)">
                    <i class="bi bi-pencil text-primary"></i>
                  </button>
                  <button class="btn-icon me-2" @click="showExerciciosModal(municipio)">
                    <i class="bi bi-gear text-secondary"></i>
                  </button>
                  <button class="btn-icon me-2" @click="showUsuariosModal(municipio)" title="Usuários vinculados">
                    <i class="bi bi-people text-primary"></i>
                  </button>
                  <button class="btn-icon" @click="showDeleteMunicipioModal(municipio)">
                    <i class="bi bi-trash text-danger"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
        <div v-if="municipios.length === 0" class="text-center text-muted mt-4">
          <p>Nenhum município encontrado.</p>
        </div>
        <div class="fila-pagination d-flex justify-content-between align-items-center mt-3" v-if="total > 0">
          <span class="text-muted small">
            Exibindo {{ municipios.length }} de {{ total }} registros
          </span>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page === 1"
              @click="goToPage(page - 1)"
            >Anterior</button>
            <span class="btn btn-sm disabled">{{ page }}</span>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page * 20 >= total"
              @click="goToPage(page + 1)"
            >Próxima</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Municipio Modal -->
  <div class="modal fade" id="municipioModal" tabindex="-1" ref="municipioModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Editar Município' : 'Adicionar Município' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveMunicipio">
            <!-- Row 1: UF + Cidade -->
            <div class="row">
              <div class="col-md-3">
                <div :class="['mb-3', { 'has-error': (!municipioForm.uf || municipioForm.uf.trim() === '') && showValidationErrors }]">
                  <label for="uf" class="form-label">UF</label>
                  <select class="form-select" id="uf" v-model="municipioForm.uf" @change="onUfChange" required :disabled="isEditing">
                    <option value="">Selecione...</option>
                    <option v-for="estado in estados" :key="estado.sigla" :value="estado.sigla">
                      {{ estado.sigla }} - {{ estado.nome }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div :class="['mb-3', { 'has-error': (!municipioForm.cidade || municipioForm.cidade.trim() === '') && showValidationErrors }]">
                  <label for="cidade" class="form-label">Cidade</label>
                  <select class="form-select" id="cidade" v-model="municipioForm.cidade" @change="onCidadeChange" required :disabled="isEditing || !municipioForm.uf">
                    <option value="">{{ municipioForm.uf ? 'Selecione a cidade...' : 'Selecione primeiro a UF' }}</option>
                    <option v-for="cidade in cidades" :key="cidade.id" :value="cidade.nome">
                      {{ cidade.nome }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Row 2: Razão Social -->
            <div class="row">
              <div class="col-md-12">
                <div :class="['mb-3', { 'has-error': (!municipioForm.razao_social || municipioForm.razao_social.trim() === '') && showValidationErrors }]">
                  <label for="razao_social" class="form-label">Razão Social</label>
                  <input type="text" class="form-control" id="razao_social" v-model="municipioForm.razao_social" required :disabled="isEditing">
                </div>
              </div>
            </div>
            
            <!-- Row 3: Códigos -->
            <div class="row">
              <div class="col-md-6">
                <div :class="['mb-3', { 'has-error': (!municipioForm.codigo_ibge || municipioForm.codigo_ibge.trim() === '') && showValidationErrors }]">
                  <label for="codigo_ibge" class="form-label">Código IBGE</label>
                  <input type="text" class="form-control" id="codigo_ibge" v-model="municipioForm.codigo_ibge" required :disabled="isEditing">
                </div>
              </div>
              <div class="col-md-6">
                <div :class="['mb-3', { 'has-error': (!municipioForm.codigo_siconfi || municipioForm.codigo_siconfi.trim() === '') && showValidationErrors }]">
                  <label for="codigo_siconfi" class="form-label">Código SICONFI</label>
                  <input type="text" class="form-control" id="codigo_siconfi" v-model="municipioForm.codigo_siconfi" required :disabled="isEditing">
                </div>
              </div>
            </div>
            
            <!-- Collapsible Additional Information Section -->
            <div class="mb-3">
              <button 
                class="btn btn-outline-secondary w-100" 
                type="button" 
                @click="toggleCollapse"
                :class="{ 'collapsed': !isCollapseOpen }">
                <i class="bi bi-chevron-down me-2" :class="{ 'rotated': isCollapseOpen }"></i>
                Informações Adicionais
              </button>
            </div>
            
            <div class="collapse" id="additionalInfo">
              <div class="card card-body">
                <!-- Address Information -->
                <div class="row">
                  <div class="col-md-8">
                    <div class="mb-3">
                      <label for="logradouro" class="form-label">Logradouro</label>
                      <input type="text" class="form-control" id="logradouro" v-model="municipioForm.logradouro">
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="mb-3">
                      <label for="numero" class="form-label">Número</label>
                      <input type="text" class="form-control" id="numero" v-model="municipioForm.numero">
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="bairro" class="form-label">Bairro</label>
                      <input type="text" class="form-control" id="bairro" v-model="municipioForm.bairro">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="cep" class="form-label">CEP</label>
                      <input type="text" class="form-control" id="cep" v-model="municipioForm.cep">
                    </div>
                  </div>
                </div>
                
                <!-- Contact Information -->
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="cnpj" class="form-label">CNPJ</label>
                      <input type="text" class="form-control" id="cnpj" v-model="municipioForm.cnpj">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="telefone" class="form-label">Telefone</label>
                      <input type="text" class="form-control" id="telefone" v-model="municipioForm.telefone">
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="email" class="form-label">E-mail</label>
                      <input type="email" class="form-control" id="email" v-model="municipioForm.email">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label for="responsavel" class="form-label">Responsável</label>
                      <input type="text" class="form-control" id="responsavel" v-model="municipioForm.responsavel">
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Demo Checkbox - Outside collapse, at bottom -->
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3 form-check">
                  <input type="checkbox" checked class="form-check-input" id="demo" v-model="municipioForm.demo">
                  <label class="form-check-label" for="demo">Município demo</label>
                </div>
                <div class="mb-3 form-check">
                  <input type="checkbox" class="form-check-input" id="pendenciaFinanceira" v-model="municipioForm.pendencia_financeira">
                  <label class="form-check-label" for="pendenciaFinanceira">Contém pendências financeiras</label>
                </div>
              </div>
            </div>
            <div class="row mb-2">
            <div class="col-md-12">
              <div class="email-input-container">
                <label
                  for="inputemailNotification"
                  class="form-label poppins-light"
                  >Digite o(s) email(s) para receber notificações do município:</label
                >
                <input
                  type="text"
                  v-model="emailNotification"
                  placeholder="Digite um email e pressione Enter"
                  class="form-control"
                  id="inputemailNotification"
                  @keyup.enter="addEmail"
                  @keyup.space="addEmail"
                  @blur="addEmail"
                />
                <div
                  v-for="email in municipioForm.emailNotificationList"
                  :key="email"
                  class="email-tag mt-2"
                >
                  {{ email }}
                  <span @click="removeEmail(email)" class="remove-email"
                    >&times;</span
                  >
                </div>
              </div>
            </div>
          </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-secondary" :disabled="!isMunicipioFormValid" @click="saveMunicipio">{{ isEditing ? 'Atualizar' : 'Criar' }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Municipio Modal -->
  <div class="modal fade" id="deleteModal" tabindex="-1" ref="deleteModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Confirmar Exclusão</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir o município <strong>{{ municipioToDelete?.razao_social || municipioToDelete?.cidade }}</strong> ({{ municipioToDelete?.codigo_siconfi }})?</p>
          <p class="text-danger">Esta ação não pode ser desfeita. Usuários vinculados com este município perderão seus acessos.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-danger" @click="deleteMunicipio">Excluir</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Usuarios Vinculados Modal -->
  <div class="modal fade" id="usuariosModal" tabindex="-1" ref="usuariosModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">Usuários Vinculados</h5>
            <div class="text-muted small">{{ currentMunicipioForUsuarios?.uf }} - {{ currentMunicipioForUsuarios?.razao_social?.replace("MUNICÍPIO DE ", "") || currentMunicipioForUsuarios?.cidade }}</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <table class="table table-striped" v-if="municipioUsuarios.length > 0">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Tipo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="usuario in municipioUsuarios" :key="usuario.email">
                <td>{{ usuario.nome }}</td>
                <td>{{ usuario.email }}</td>
                <td>
                  <span class="badge" :class="usuario.role === 'admin' ? 'bg-danger' : 'bg-primary'">
                    {{ usuario.role === 'admin' ? 'Admin' : 'Usuário' }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="usuario.inativo ? 'bg-secondary' : 'bg-success'">
                    {{ usuario.inativo ? 'Inativo' : 'Ativo' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center text-muted mt-4">
            <p>Nenhum usuário vinculado a este município.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Fechar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Exercicios Management Modal -->
  <div class="modal fade" id="exerciciosModal" tabindex="-1" ref="exerciciosModal">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">Gerenciar Exercícios</h5>
            <div class="text-muted small">{{ currentMunicipio?.uf }} - {{ currentMunicipio?.razao_social?.replace("MUNICÍPIO DE ", "") || currentMunicipio?.cidade }}</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Exercícios Cadastrados</h6>
            <button type="button" class="btn btn-secondary" @click="showAddExercicioModal">
              Novo Exercício
            </button>
          </div>
          
          <div class="exercicios-table">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Exercício</th>
                  <th>LRF RGF Simplificado</th>
                  <th>Upload Liberado</th>
                  <th>Link análises SICONFI</th>
                  <th>Link análises CAPAG</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="exercicio in exercicios" :key="exercicio.ano">
                  <td>{{ exercicio.ano }}</td>
                  <td>
                    <span class="badge" :class="exercicio.lrf_rgf_simplificado ? 'bg-success' : 'bg-secondary'">
                      {{ exercicio.lrf_rgf_simplificado ? 'Sim' : 'Não' }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="getUploadStatusClass(exercicio)">
                      {{ getUploadStatusText(exercicio) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="text-cell">
                      <span class="text-content" :title="exercicio.report || '-'">
                        {{ exercicio.report || '-' }}
                      </span>
                      <button 
                        :title="exercicio.report || '-'"
                        class="btn-copy" 
                        @click="copyToClipboard(exercicio.report)">
                        <i class="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div class="text-cell">
                      <span class="text-content" :title="exercicio.report_capag || '-'">
                        {{ exercicio.report_capag || '-' }}
                      </span>
                      <button 
                        :title="exercicio.report_capag || '-'"
                        class="btn-copy" 
                        @click="copyToClipboard(exercicio.report_capag)">
                        <i class="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center justify-content-end">
                      <button class="btn-icon me-2" @click="showEditExercicioModal(exercicio)">
                        <i class="bi bi-pencil text-primary"></i>
                      </button>
                      <button 
                        class="btn-icon me-2" 
                        @click="createFolderStructure(exercicio)"
                        :disabled="currentMunicipio?.demo || exercicio.upload_liberado || isCreatingFolderStructure"
                        :title="getFolderStructureButtonTitle(exercicio)"
                      >
                        <i class="bi bi-folder-plus text-secondary" v-if="!isCreatingFolderStructure"></i>
                        <div class="spinner-border spinner-border-sm text-secondary" role="status" v-else>
                          <span class="visually-hidden">Criando...</span>
                        </div>
                      </button>
                      <button class="btn-icon" @click="showDeleteExercicioModal(exercicio)">
                        <i class="bi bi-trash text-danger"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="exercicios.length === 0" class="text-center text-muted mt-4">
              <p>Nenhum exercício cadastrado.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Fechar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Exercicio Edit/Create Modal -->
  <div class="modal modal-xl fade" id="exercicioModal" tabindex="-1" ref="exercicioModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditingExercicio ? 'Editar Exercício' : 'Novo Exercício' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveExercicio">
            <div class="mb-3">
              <label for="exercicio" class="form-label">Exercício (Ano)</label>
              <input type="text" class="form-control" id="exercicio" v-model="exercicioForm.exercicio" required :disabled="isEditingExercicio">
            </div>
            <div class="mb-3">
              <label for="report" class="form-label">Link análises SICONFI</label>
              <textarea class="form-control" id="report" v-model="exercicioForm.report" rows="3"></textarea>
            </div>
            <div class="mb-3">
              <label for="print" class="form-label">Link exportação pdf das análises SICONFI</label>
              <textarea class="form-control" id="print" v-model="exercicioForm.print" rows="3"></textarea>
            </div>
            <div class="mb-3">
              <label for="report" class="form-label">Link análises CAPAG</label>
              <textarea class="form-control" id="report_capag" v-model="exercicioForm.report_capag" rows="3"></textarea>
            </div>
            <div class="mb-3">
              <label for="print" class="form-label">Link exportação pdf das análises CAPAG</label>
              <textarea class="form-control" id="print_capag" v-model="exercicioForm.print_capag" rows="3"></textarea>
            </div>
            <div class="mb-3 form-check">
              <input type="checkbox" class="form-check-input" id="lrf_rgf_simplificado" v-model="exercicioForm.lrf_rgf_simplificado">
              <label class="form-check-label" for="lrf_rgf_simplificado">LRF RGF Simplificado</label>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-secondary" @click="saveExercicio">{{ isEditingExercicio ? 'Atualizar' : 'Criar' }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Exercicio Modal -->
  <div class="modal fade" id="deleteExercicioModal" tabindex="-1" ref="deleteExercicioModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Confirmar Exclusão</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir o exercício <strong>{{ exercicioToDelete?.ano }}</strong>?</p>
          <p class="text-danger">Esta ação não pode ser desfeita.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-danger" @click="deleteExercicio">Excluir</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosApi from "@/axios";
import Nav from "@/components/Nav.vue";
import { mapState, mapActions } from "vuex";
// Modal and Collapse are available via window.bootstrap from CDN

export default {
  name: "Municipios",
  data() {
    return {
      municipios: [],
      searchText: '',
      page: 1,
      total: 0,
      showOnlyOfficial: false,
      isEditing: false,
      emailNotification: "",
      municipioForm: {
        razao_social: '',
        cidade: '',
        uf: '',
        codigo_ibge: '',
        codigo_siconfi: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cep: '',
        cnpj: '',
        telefone: '',
        email: '',
        responsavel: '',
        demo: true,
        pendencia_financeira: false,
        emailNotificationList: []
      },
      municipioToDelete: null,
      estados: [],
      cidades: [],
      loadingCidades: false,
      isCollapseOpen: false,
      // Usuarios vinculados
      municipioUsuarios: [],
      currentMunicipioForUsuarios: null,
      // Exercicios management
      currentMunicipio: null,
      exercicios: [],
      exercicioForm: {
        exercicio: '',
        report: '',
        print: '',
        lrf_rgf_simplificado: false,
        report_capag: '',
        print_capag: ''
      },
      isEditingExercicio: false,
      exercicioToDelete: null,
      showValidationErrors: false,
      isCreatingFolderStructure: false,
    };
  },
  computed: {
    ...mapState(["user", "isAuthLoading"]),
    isMunicipioFormValid() {
      const isUfValid = this.municipioForm.uf && this.municipioForm.uf.trim() !== '';
      const isRazaoSocialValid = this.municipioForm.razao_social && this.municipioForm.razao_social.trim() !== '';
      const isCidadeValid = this.municipioForm.cidade && this.municipioForm.cidade.trim() !== '';
      const isCodigoIbgeValid = this.municipioForm.codigo_ibge && this.municipioForm.codigo_ibge.trim() !== '';
      const isCodigoSiconfiValid = this.municipioForm.codigo_siconfi && this.municipioForm.codigo_siconfi.trim() !== '';
      
      return isUfValid && isRazaoSocialValid && isCidadeValid && isCodigoIbgeValid && isCodigoSiconfiValid;
    },
  },
  watch: {
    showOnlyOfficial() {
      this.page = 1;
      this.getMunicipios();
    },
  },
  async created() {
    try {
      await this.validateToken();
      this.checkUserEntidade();
      
      // Wait for auth loading to complete before proceeding
      if (this.isAuthLoading) {
        const checkAuth = () => {
          if (!this.isAuthLoading) {
            this.getMunicipios();
            this.loadEstados();
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      } else {
        this.getMunicipios();
        this.loadEstados();
      }
    } catch (error) {
      console.log(error);
      
      if (error.message === "USER_DELETED") {
        this.showToast({
          title: 'Sessão Encerrada',
          message: 'Sua conta foi removida do sistema. Entre em contato com o administrador.',
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
      
      this.$router.push("/login");
    }
  },
  methods: {
    ...mapActions(["validateToken", "showToast"]),
    async getMunicipios() {
      try {
        const params = new URLSearchParams({ page: this.page });
        if (this.searchText.trim()) params.set('search', this.searchText.trim());
        if (this.showOnlyOfficial) params.set('oficiaisOnly', 'true');
        const response = await axiosApi.get(`/api/entidades?${params}`, { withCredentials: true });
        this.municipios = response.data.data || [];
        this.total = response.data.pagination?.total ?? 0;
      } catch (error) {
        console.error(error);
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar municípios: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    checkUserEntidade() {
      if (!this.$store.state.userEntidade) {
        this.$router.push("/selecaoEntidades");
      }
    },
    showAddMunicipioModal() {
      this.isEditing = false;
      this.resetForm();
      this.showValidationErrors = false;
      this.showModal('municipioModal');
    },
    search() {
      this.page = 1;
      this.getMunicipios();
    },
    goToPage(p) {
      this.page = p;
      this.getMunicipios();
    },
    async showEditMunicipioModal(municipio) {
      this.isEditing = true;
      this.municipioForm = {
        razao_social: municipio.razao_social || '',
        cidade: municipio.cidade || '',
        uf: municipio.uf || '',
        codigo_ibge: municipio.codigo_ibge || '',
        codigo_siconfi: municipio.codigo_siconfi || '',
        logradouro: municipio.logradouro || '',
        numero: municipio.numero || '',
        bairro: municipio.bairro || '',
        cep: municipio.cep || '',
        cnpj: municipio.cnpj || '',
        telefone: municipio.telefone || '',
        email: municipio.email || '',
        responsavel: municipio.responsavel || '',
        demo: municipio.demo || false,
        pendencia_financeira: municipio.pendencia_financeira || false,
        emailNotificationList: municipio.email_notificacoes || []
      };

      // Load cities for the selected UF when editing
      if (this.municipioForm.uf) {
        await this.loadCidades(this.municipioForm.uf);
      }

      this.showValidationErrors = false;
      this.showModal('municipioModal');
    },
    showDeleteMunicipioModal(municipio) {
      this.municipioToDelete = municipio;
      this.showModal('deleteModal');
    },
    async showUsuariosModal(municipio) {
      this.currentMunicipioForUsuarios = municipio;
      this.municipioUsuarios = [];
      this.showModal('usuariosModal');
      try {
        const response = await axiosApi.get(`/api/entidades/${municipio.codigo_siconfi}/usuarios`, { withCredentials: true });
        this.municipioUsuarios = response.data.data || [];
      } catch (error) {
        console.error(error);
        this.hideModal('usuariosModal');
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar usuários: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    showModal(modalId) {
      const modal = new window.bootstrap.Modal(document.getElementById(modalId));
      modal.show();
    },
    hideModal(modalId) {
      const modal = window.bootstrap.Modal.getInstance(document.getElementById(modalId));
      if (modal) {
        modal.hide();
      }
    },
    resetForm() {
      this.municipioForm = {
        razao_social: '',
        cidade: '',
        uf: '',
        codigo_ibge: '',
        codigo_siconfi: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cep: '',
        cnpj: '',
        telefone: '',
        email: '',
        responsavel: '',
        demo: true,
        pendencia_financeira: false,
        emailNotificationList: []
      };
      this.cidades = [];
    },
    async loadEstados() {
      try {
        const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
        const estados = await response.json();
        this.estados = estados.sort((a, b) => a.nome.localeCompare(b.nome));
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar lista de estados',
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    async loadCidades(uf) {
      try {
        this.loadingCidades = true;
        const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
        const cidades = await response.json();
        this.cidades = cidades.sort((a, b) => a.nome.localeCompare(b.nome));
      } catch (error) {
        console.error('Erro ao carregar cidades:', error);
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar lista de cidades',
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
        this.cidades = [];
      } finally {
        this.loadingCidades = false;
      }
    },
    async onUfChange() {
      this.municipioForm.cidade = '';
      this.municipioForm.codigo_ibge = '';
      this.municipioForm.codigo_siconfi = '';
      this.cidades = [];
      
      if (this.municipioForm.uf) {
        await this.loadCidades(this.municipioForm.uf);
      }
    },
    async onCidadeChange() {
      if (this.municipioForm.cidade && this.municipioForm.uf) {
        const cidade = this.cidades.find(c => c.nome === this.municipioForm.cidade);
        if (cidade) {
          this.municipioForm.codigo_ibge = cidade.id.toString();
          this.municipioForm.codigo_siconfi = cidade.id.toString() + 'EX';
          
          // Always suggest razao_social based on selected cidade (can be manually changed)
          this.municipioForm.razao_social = `MUNICÍPIO DE ${this.municipioForm.cidade.toUpperCase()}`;
        }
      } else {
        this.municipioForm.codigo_ibge = '';
        this.municipioForm.codigo_siconfi = '';
        this.municipioForm.razao_social = '';
      }
    },
    async saveMunicipio() {
      try {
        // Check if form is valid
        if (!this.isMunicipioFormValid) {
          this.showValidationErrors = true;
          return;
        }
        
        this.showValidationErrors = false;

        const invalidEmails = (this.municipioForm.emailNotificationList || []).filter(e => !this.isValidEmail(e));
        if (invalidEmails.length > 0) {
          this.showToast({
            title: 'Emails inválidos',
            message: `Os seguintes emails são inválidos: ${invalidEmails.join(', ')}`,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
          return;
        }

        const municipioData = {
          ...this.municipioForm,
          exercicios: {} // Empty exercicios - user will add them later
        };

        let response;
        if (this.isEditing) {
          response = await axiosApi.put(`/api/entidades/${this.municipioForm.codigo_siconfi}`, municipioData, { withCredentials: true });
        } else {
          response = await axiosApi.post('/api/entidades', municipioData, { withCredentials: true });
        }

        if (response.data.success) {
          const municipioName = this.municipioForm.razao_social || this.municipioForm.cidade;
          const wasEditing = this.isEditing;
          const municipioData = { ...this.municipioForm };
          
          this.hideModal('municipioModal');
          this.getMunicipios();
          this.showToast({
            title: 'Sucesso',
            message: wasEditing ? 
              `Município ${municipioName} atualizado com sucesso!` : 
              `Município ${municipioName} criado com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.resetForm();
          
          // Show exercicios modal for new municipalities
          if (!wasEditing) {
            setTimeout(() => {
              this.showExerciciosModalForNewMunicipio(municipioData);
            }, 500);
          }
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error saving municipio:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao salvar município: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    async deleteMunicipio() {
      try {
        const response = await axiosApi.delete(`/api/entidades/${this.municipioToDelete.codigo_siconfi}`, { withCredentials: true });
        
        if (response.data.success) {
          const municipioName = this.municipioToDelete.razao_social || this.municipioToDelete.cidade;
          
          this.hideModal('deleteModal');
          this.getMunicipios();
          this.showToast({
            title: 'Sucesso',
            message: `Município ${municipioName} excluído com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.municipioToDelete = null;
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error deleting municipio:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao excluir município: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    toggleCollapse() {
      const collapseElement = document.getElementById('additionalInfo');
      
      // 1. Instantiate the Element from bootstrap using new Collapse
      if (!collapseElement._collapse) {
        collapseElement._collapse = new window.bootstrap.Collapse(collapseElement, { toggle: false });
      }
      
      // 4. Check if element has 'show' class to determine action
      if (collapseElement.classList.contains('show')) {
        // 3. If it has 'show' class, invoke .hide() method
        collapseElement._collapse.hide();
        this.isCollapseOpen = false;
      } else {
        // 2. If it doesn't have 'show' class, invoke .show() method
        collapseElement._collapse.show();
        this.isCollapseOpen = true;
      }
    },
    clearFilter() {
      this.searchText = '';
      this.showOnlyOfficial = false;
      this.page = 1;
      this.getMunicipios();
    },
    // Exercicios management methods
    async showExerciciosModal(municipio) {
      this.currentMunicipio = municipio;
      await this.loadExercicios();
      this.showModal('exerciciosModal');
    },
    async showExerciciosModalForNewMunicipio(municipio) {
      this.currentMunicipio = municipio;
      this.exercicios = [];
      this.showModal('exerciciosModal');
    },
    async loadExercicios() {
      try {
        const response = await axiosApi.get(`/api/entidades/${this.currentMunicipio.codigo_siconfi}`, { withCredentials: true });
        if (response.data.success && response.data.data.exercicios) {
          // Convert exercicios object to array
          this.exercicios = Object.keys(response.data.data.exercicios).map(ano => ({
            ano: ano,
            ...response.data.data.exercicios[ano]
          })).sort((a, b) => b.ano.localeCompare(a.ano));
        } else {
          this.exercicios = [];
        }
      } catch (error) {
        console.error('Error loading exercicios:', error);
        this.exercicios = [];
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar exercícios: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    showAddExercicioModal() {
      this.isEditingExercicio = false;
      this.resetExercicioForm();
      this.showModal('exercicioModal');
    },
    showEditExercicioModal(exercicio) {
      this.isEditingExercicio = true;
      this.exercicioForm = {
        exercicio: exercicio.ano,
        report: exercicio.report || '',
        print: exercicio.print || '',
        lrf_rgf_simplificado: exercicio.lrf_rgf_simplificado || false,
        report_capag: exercicio.report_capag || '',
        print_capag: exercicio.print_capag || ''
      };
      this.showModal('exercicioModal');
    },
    resetExercicioForm() {
      this.exercicioForm = {
        exercicio: '',
        report: '',
        print: '',
        lrf_rgf_simplificado: false,
        report_capag: '',
        print_capag: ''
      };
    },
    async saveExercicio() {
      try {
        const exercicioData = {
          exercicio: this.exercicioForm.exercicio,
          report: this.exercicioForm.report,
          print: this.exercicioForm.print,
          lrf_rgf_simplificado: this.exercicioForm.lrf_rgf_simplificado,
          report_capag: this.exercicioForm.report_capag,
          print_capag: this.exercicioForm.print_capag,
        };

        let response;
        if (this.isEditingExercicio) {
          // Update existing exercicio
          response = await axiosApi.put(`/api/entidades/${this.currentMunicipio.codigo_siconfi}/exercicio/${this.exercicioForm.exercicio}`, exercicioData, { withCredentials: true });
        } else {
          // Create new exercicio
          response = await axiosApi.post(`/api/entidades/${this.currentMunicipio.codigo_siconfi}/add-exercicio`, exercicioData, { withCredentials: true });
        }

        if (response.data.success) {
          this.hideModal('exercicioModal');
          await this.loadExercicios();
          this.showToast({
            title: 'Sucesso',
            message: this.isEditingExercicio ? 
              `Exercício ${this.exercicioForm.exercicio} atualizado com sucesso!` : 
              `Exercício ${this.exercicioForm.exercicio} criado com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.resetExercicioForm();
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error saving exercicio:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao salvar exercício: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    showDeleteExercicioModal(exercicio) {
      this.exercicioToDelete = exercicio;
      this.showModal('deleteExercicioModal');
    },
    async deleteExercicio() {
      try {
        const response = await axiosApi.delete(`/api/entidades/${this.currentMunicipio.codigo_siconfi}/exercicio/${this.exercicioToDelete.ano}`, { withCredentials: true });
        
        if (response.data.success) {
          const exercicioAno = this.exercicioToDelete.ano;
          this.hideModal('deleteExercicioModal');
          await this.loadExercicios();
          this.showToast({
            title: 'Sucesso',
            message: `Exercício ${exercicioAno} excluído com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.exercicioToDelete = null;
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error deleting exercicio:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao excluir exercício: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        console.error('Erro ao copiar texto:', error);
      }
    },
    // Upload status methods
    getUploadStatusText(exercicio) {
      if (this.currentMunicipio?.demo) {
        return 'Bloqueado';
      }
      return exercicio.upload_liberado ? 'Liberado' : 'Pendente';
    },
    getUploadStatusClass(exercicio) {
      if (this.currentMunicipio?.demo) {
        return 'bg-danger';
      }
      return exercicio.upload_liberado ? 'bg-success' : 'bg-warning text-dark';
    },
    getFolderStructureButtonTitle(exercicio) {
      if (this.currentMunicipio?.demo) {
        return 'Não disponível para municípios demo';
      }
      if (exercicio.upload_liberado) {
        return 'Estrutura de pastas já criada';
      }
      return 'Criar estrutura de pastas';
    },
    async createFolderStructure(exercicio) {
      if (this.currentMunicipio?.demo || exercicio.upload_liberado || this.isCreatingFolderStructure) {
        return;
      }

      this.isCreatingFolderStructure = true;

      try {
        console.log('Creating folder structure for:', {
          codigo_siconfi: this.currentMunicipio.codigo_siconfi,
          exercicio: exercicio.ano,
          lrf_rgf_simplificado: exercicio.lrf_rgf_simplificado
        });

        const response = await axiosApi.post(
          `/api/entidades/${this.currentMunicipio.codigo_siconfi}/exercicio/${exercicio.ano}/create-folder-structure`,
          {
            lrf_rgf_simplificado: exercicio.lrf_rgf_simplificado
          },
          { withCredentials: true }
        );

        if (response.data.success) {
          const jobId = response.data.data.jobId;
          
          this.showToast({
            title: 'Estrutura de Pastas',
            message: `Job iniciado para criar estrutura de pastas do exercício ${exercicio.ano}. Job ID: ${jobId}`,
            type: 'toast-info',
            headerClass: 'toast-header-info'
          });

          // Start polling for job completion
          this.pollJobStatus(jobId, exercicio);
        }
      } catch (error) {
        console.error('Error creating folder structure:', error);
        this.showToast({
          title: 'Erro',
          message: 'Erro ao criar estrutura de pastas: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
        this.isCreatingFolderStructure = false;
      }
    },
    async pollJobStatus(jobId, exercicio) {
      const pollInterval = 3000; // Poll every 3 seconds
      const maxAttempts = 60; // Max 3 minutes (60 * 3 seconds)
      let attempts = 0;

      const poll = async () => {
        attempts++;
        
        try {
          const response = await axiosApi.get(`/api/entidades/folder-structure/status/${jobId}`, {
            withCredentials: true
          });

          if (response.data.success) {
            const status = response.data.data.status;
            
            if (status === 'COMPLETED') {
              // Refresh exercicios to get updated upload_liberado flag
              await this.loadExercicios();
              
              this.showToast({
                title: 'Sucesso',
                message: `Estrutura de pastas criada com sucesso para o exercício ${exercicio.ano}!`,
                type: 'toast-success',
                headerClass: 'toast-header-success'
              });
              
              this.isCreatingFolderStructure = false;
              return;
            } else if (status === 'FAILED') {
              const errorMessage = response.data.data.error_message || 'Erro desconhecido';
              
              this.showToast({
                title: 'Erro',
                message: `Falha ao criar estrutura de pastas para o exercício ${exercicio.ano}: ${errorMessage}`,
                type: 'toast-danger',
                headerClass: 'toast-header-danger'
              });
              
              this.isCreatingFolderStructure = false;
              return;
            } else if (status === 'PROCESSING' || status === 'PENDING') {
              // Continue polling
              if (attempts < maxAttempts) {
                setTimeout(poll, pollInterval);
              } else {
                this.showToast({
                  title: 'Timeout',
                  message: `Timeout ao aguardar conclusão do job para o exercício ${exercicio.ano}. Verifique o status manualmente.`,
                  type: 'toast-warning',
                  headerClass: 'toast-header-warning'
                });
                
                this.isCreatingFolderStructure = false;
              }
            }
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          this.showToast({
            title: 'Erro',
            message: 'Erro ao verificar status do job: ' + (error.response?.data?.message || error.message),
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
          
          this.isCreatingFolderStructure = false;
        }
      };

      setTimeout(poll, pollInterval);
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    addEmail() {
      const email = this.emailNotification.trim();
      if (!email) {
        this.emailNotification = "";
        return;
      }
      if (!this.isValidEmail(email)) {
        this.showToast({
          title: 'Email inválido',
          message: `"${email}" não é um email válido`,
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
        this.emailNotification = "";
        return;
      }
      if (!this.municipioForm.emailNotificationList) {
        this.municipioForm.emailNotificationList = [];
      }
      if (!this.municipioForm.emailNotificationList.includes(email)) {
        this.municipioForm.emailNotificationList.push(email);
      }
      this.emailNotification = "";
    },
    removeEmail(email) {
      if (!this.municipioForm.emailNotificationList) {
        this.municipioForm.emailNotificationList = [];
        return;
      }
      this.municipioForm.emailNotificationList = this.municipioForm.emailNotificationList.filter((e) => e !== email);
    },
  },
  components: {
    Nav,
  },
};
</script>

<style>
.table {
  margin-top: 20px;
}

.btn-icon {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
}

.btn-icon:hover {
  opacity: 0.7;
}

.municipio-filter-input {
  max-width: 280px;
  min-width: 200px;
}

.municipio-container {
  margin-bottom: 2rem;
  min-width: 1200px;
}

@media (max-width: 1260px) {
  .municipio-container {
    min-width: 0;
    width: 100%;
  }
}

.municipio-header {
  margin-bottom: 1rem;
}

.municipio-header h1 {
  color: var(--azul-final, #1976d2);
  font-weight: 600;
}

/* Collapse button styles */
.bi-chevron-down {
  -webkit-transition: -webkit-transform 0.3s ease, transform 0.3s ease;
  -moz-transition: -moz-transform 0.3s ease, transform 0.3s ease;
  -o-transition: -o-transform 0.3s ease, transform 0.3s ease;
  transition: transform 0.3s ease;
  
  -webkit-transform: rotate(0deg);
  -moz-transform: rotate(0deg);
  -ms-transform: rotate(0deg);
  -o-transform: rotate(0deg);
  transform: rotate(0deg);
  
  /* Ensure the element is transformable */
  display: inline-block;
  transform-origin: center center;
}

.bi-chevron-down.rotated {
  -webkit-transform: rotate(180deg);
  -moz-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  -o-transform: rotate(180deg);
  transform: rotate(180deg);
}

/* Additional info card styling */
#additionalInfo .card-body {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

/* Municipios table fixed column widths */
.municipios-table {
  table-layout: fixed;
  width: 100%;
}

.municipios-table .municipio-col {
  width: 45%;
}

.municipios-table .ibge-col {
  width: 15%;
  text-align: center;
}

.municipios-table .demo-col {
  width: 15%;
  text-align: center;
}

.municipios-table .actions-col {
  width: 25%;
  text-align: right;
}

/* Ensure table cells maintain consistent widths */
.municipios-table td.municipio-col {
  width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.municipios-table td.ibge-col {
  width: 15%;
  text-align: center;
}

.municipios-table td.demo-col {
  width: 15%;
  text-align: center;
}

.municipios-table td.actions-col {
  width: 25%;
  text-align: right;
}

/* Exercicios table text clipping and copy functionality */
.text-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 300px;
}

.text-content {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.btn-copy {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  color: #6c757d;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-copy:hover {
  background-color: #e9ecef;
  color: #495057;
}

.btn-copy:active {
  transform: scale(0.95);
}

.exercicios-table {
  overflow-x: auto;
}

.exercicios-table table {
  min-width: 100%;
  table-layout: fixed;
}

.exercicios-table th:nth-child(1) { width: 100px; }
.exercicios-table th:nth-child(2) { width: 200px; }
.exercicios-table th:nth-child(3) { width: 150px; }
.exercicios-table th:nth-child(4) { width: 275px; }
.exercicios-table th:nth-child(5) { width: 275px; }
.exercicios-table th:nth-child(6) { width: 90px; }

/* Form validation styling */
.has-error .form-control,
.has-error .form-select {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.has-error .form-label {
  color: #dc3545;
}


.small-officials-checkbox{
  width: 0.8rem;
  height: 0.8rem;
}

.small-label-officials-checkbox{
  font-size: 0.8rem;
}

.email-input-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
</style>
